import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { takeIntent } from './postLoginRedirect';
import AppIDLogin from './AppIDLogin';
import ProtectedRoute from './ProtectedRoute';
import MainGrid from './MainGrid';
import SignUpForm from './SignUpForm';
import VerifySignUp from './VerifySignUp';

// Code-split the non-landing routes so their (sometimes heavy) dependencies are
// not parsed on the initial MainGrid load. In particular Protein pulls in
// ag-grid-enterprise (~1.7MB of source), which used to inflate the single main
// bundle and slow down every page — including the grid landing page.
const Protein = lazy(() => import('./Protein'));
const Studies = lazy(() => import('./Studies'));
const Study = lazy(() => import('./Study'));
const Admin = lazy(() => import('./Admin'));
const PublicationsList = lazy(() => import('./admin/PublicationsList'));
const PublicationEditor = lazy(() => import('./admin/PublicationEditor'));
const StudySheetsManager = lazy(() => import('./admin/StudySheetsManager'));
const NewsManager = lazy(() => import('./admin/NewsManager'));
const About = lazy(() => import('./About'));
const Help = lazy(() => import('./Help'));

// Sends a freshly signed-in visitor on to wherever they were originally
// heading. The App ID round trip always returns to the front page, so without
// this a QR code pointing at /studies would strand a signed-out visitor on the
// protein grid. Renders nothing.
function PendingDestination() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading || !user) return;
    const target = takeIntent();
    if (target && target !== `${location.pathname}${location.search || ''}`) {
      navigate(target, { replace: true });
    }
  }, [user, isLoading, navigate, location]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PendingDestination />
        <Suspense fallback={null}>
        <Routes>
          <Route path="/signup" element={<SignUpForm />} />
          <Route path="/verify-signup" element={<VerifySignUp />} />
          <Route path="/login" element={<AppIDLogin />} />
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <About />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            }
          />
          <Route
            path="/protein/:uniprotId/"
            element={
              <ProtectedRoute>
                <Protein />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studies"
            element={
              <ProtectedRoute>
                <Studies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/study/:doi"
            element={
              <ProtectedRoute>
                <Study />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/publications"
            element={
              <ProtectedRoute requireAdmin>
                <PublicationsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/publications/:doi"
            element={
              <ProtectedRoute requireAdmin>
                <PublicationEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/news"
            element={
              <ProtectedRoute requireAdmin>
                <NewsManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/study-sheets"
            element={
              <ProtectedRoute requireAdmin>
                <StudySheetsManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainGrid />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
